import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Divider,
  InputBase,
  Stack,
  Typography,
} from "@mui/material";

import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import SearchIcon from "@mui/icons-material/Search";

import { useEffect, useMemo, useState } from "react";
import {
  fetchRolePermissions,
  fetchUserPermissions,
  updateRolePermissions,
  updateUserPermissions,
} from "../../services/permissionService";
import { fetchRoles } from "../../services/roleService";
import { fetchUserList } from "../../services/userService";

/* ─── design tokens ────────────────────────────────────── */
const P = {
  bg: "#F7F8FA",
  surface: "#FFFFFF",
  border: "#E8EAF0",
  borderStrong: "#CDD1DC",
  text: "#111827",
  textMid: "#4B5563",
  textSoft: "#9CA3AF",
  accent: "#2563EB",
  accentLight: "#EFF4FF",
  accentBorder: "#BFCFFF",
  green: "#059669",
  greenLight: "#ECFDF5",
  greenBorder: "#A7F3D0",
  red: "#DC2626",
  redLight: "#FEF2F2",
  redBorder: "#FCA5A5",
  amber: "#D97706",
  amberLight: "#FFFBEB",
  amberBorder: "#FCD34D",
};

/* ─── Avatar ────────────────────────────────────────────── */
const AVATAR_COLORS: [string, string][] = [
  ["#EDE9FE", "#6D28D9"],
  ["#D1FAE5", "#065F46"],
  ["#DBEAFE", "#1D4ED8"],
  ["#FCE7F3", "#9D174D"],
  ["#FEF3C7", "#92400E"],
  ["#FFEDD5", "#9A3412"],
];

function getInitials(name: string = "") {
  return (
    name
      .trim()
      .split(" ")
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() ?? "")
      .join("") || "?"
  );
}

function Avatar({ name = "", size = 36 }: { name?: string; size?: number }) {
  const safeName = name.trim() || "?";

  const colorIndex = safeName.charCodeAt(0) % AVATAR_COLORS.length;

  const [bg, fg] = AVATAR_COLORS[colorIndex];

  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: bg,
        color: fg,
        fontSize: size * 0.36,
        fontWeight: 700,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        letterSpacing: "0.02em",
      }}
    >
      {getInitials(safeName)}
    </Box>
  );
}

/* ─── StatusPill ────────────────────────────────────────── */
type PillVariant = "green" | "red" | "blue" | "gray";

const PILL: Record<PillVariant, { bg: string; color: string; border: string }> =
  {
    green: { bg: P.greenLight, color: P.green, border: P.greenBorder },
    red: { bg: P.redLight, color: P.red, border: P.redBorder },
    blue: { bg: P.accentLight, color: P.accent, border: P.accentBorder },
    gray: { bg: "#F3F4F6", color: P.textMid, border: P.border },
  };

function StatusPill({
  label,
  variant,
}: {
  label: string;
  variant: PillVariant;
}) {
  const s = PILL[variant];
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: "8px",
        py: "2px",
        borderRadius: "20px",
        background: s.bg,
        border: `1px solid ${s.border}`,
        color: s.color,
        fontSize: 11,
        fontWeight: 600,
        lineHeight: "18px",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Box>
  );
}

/* ─── TypeBadge ─────────────────────────────────────────── */
function TypeBadge({ label }: { label: string }) {
  return (
    <Box
      sx={{
        px: "6px",
        py: "1px",
        background: "#F3F4F6",
        border: `1px solid ${P.border}`,
        borderRadius: "4px",
        fontSize: 10,
        fontWeight: 700,
        color: P.textMid,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
      }}
    >
      {label}
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
════════════════════════════════════════════════════════════ */
const PermissionManagement = () => {
  const [activeTab, setActiveTab] = useState<"roles" | "users">("roles");
  const [roles, setRoles] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [search, setSearch] = useState("");

  const [selectedType, setSelectedType] = useState<"role" | "user">("role");
  const [selectedId, setSelectedId] = useState("");
  const [permissions, setPermissions] = useState<any[]>([]);
  const [changedPermissions, setChangedPermissions] = useState<
    Record<string, any>
  >({});

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedOk, setSavedOk] = useState(false);

  // track which modules are COLLAPSED (default = all expanded)
  const [collapsedModules, setCollapsedModules] = useState<
    Record<string, boolean>
  >({});

  /* ── load lists on mount ── */
  useEffect(() => {
    fetchRoles().then(setRoles);
    fetchUserList("").then(setUsers);
  }, []);

  /* ── filtered sidebar list ── */
  const listData = useMemo(() => {
    const raw = activeTab === "roles" ? roles : users;
    const q = search.toLowerCase();
    if (!raw || raw.length === 0) return [];
    return raw.filter((item: any) => {
      const name =
        activeTab === "roles"
          ? item.name
          : `${item.first_name} ${item.last_name}`;
      return name.toLowerCase().includes(q);
    });
  }, [activeTab, roles, users, search]);

  /* ── selected entity display name ── */
  const selectedName = useMemo(() => {
    if (!selectedId) return "";
    if (activeTab === "roles") {
      return roles.find((r) => r.role_id === selectedId)?.name ?? "";
    }
    const u = users.find((u) => u.user_id === selectedId);
    return u ? `${u.first_name} ${u.last_name}` : "";
  }, [selectedId, activeTab, roles, users]);

  /* ── group permissions by module ── */
  const groupedPermissions = useMemo(() => {
    return permissions.reduce((acc: Record<string, any[]>, p: any) => {
      if (!acc[p.module]) acc[p.module] = [];
      acc[p.module].push(p);
      return acc;
    }, {});
  }, [permissions]);

  const changedCount = Object.keys(changedPermissions).length;

  /* ── load permissions ── */
  const loadPermissions = async (type: "role" | "user", id: string) => {
    setLoading(true);
    setSelectedType(type);
    setSelectedId(id);
    setChangedPermissions({});
    setSavedOk(false);
    setCollapsedModules({}); // reset: all modules expanded on new selection

    const data =
      type === "role"
        ? await fetchRolePermissions(id)
        : await fetchUserPermissions(id);

    setPermissions(data ?? []);
    setLoading(false);
  };

  /* ── save ── */
  const save = async () => {
    if (changedCount === 0) return;
    setSaving(true);

    if (selectedType === "role") {
      const ids = permissions
        .filter((p) => p.enabled)
        .map((p) => p.permission_id);
      await updateRolePermissions(selectedId, ids);
    } else {
      const payload = Object.values(changedPermissions).map((p: any) => ({
        permission_id: p.permission_id,
        allowed: p.allowed ?? null,
      }));
      await updateUserPermissions(selectedId, payload);
    }

    setChangedPermissions({});
    setSaving(false);
    setSavedOk(true);
    setTimeout(() => setSavedOk(false), 3000);
  };

  /* ── role permission toggle (same logic as original) ── */
  const toggleRolePermission = (id: string) => {
    setPermissions((prev) => {
      const next = prev.map((p) =>
        p.permission_id === id ? { ...p, enabled: !p.enabled } : p,
      );
      const changed = next.find((p) => p.permission_id === id);
      if (changed) setChangedPermissions((c) => ({ ...c, [id]: changed }));
      return next;
    });
  };

  /* ── user override (same logic as original) ── */
  const setUserOverride = (id: string, value: "inherit" | "allow" | "deny") => {
    setPermissions((prev) => {
      const next = prev.map((p) =>
        p.permission_id === id
          ? {
              ...p,
              override_type: value,
              allowed: value === "inherit" ? null : value === "allow",
            }
          : p,
      );
      const changed = next.find((p) => p.permission_id === id);
      if (changed) setChangedPermissions((c) => ({ ...c, [id]: changed }));
      return next;
    });
  };

  /* ── toggle collapse (click header to collapse, not expand) ── */
  const toggleModule = (mod: string) =>
    setCollapsedModules((prev) => ({ ...prev, [mod]: !prev[mod] }));

  /* ════════════════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════════════════ */
  return (
    <Box sx={{ background: P.bg, p: { xs: 2, md: 3 } }}>
      {/* Page header */}
      <Stack mb={2.5} direction={"row"} gap={2}>
        <Stack direction={"column"}>
          <Typography
            sx={{
              fontSize: 22,
              fontWeight: 700,
              color: P.text,
              letterSpacing: "-0.3px",
            }}
          >
            Permission Management
          </Typography>
          <Typography sx={{ fontSize: 13, color: P.textSoft, mt: 0.5 }}>
            Control access to modules and features across your organization
          </Typography>
        </Stack>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexGrow: 1,
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "10px",
            background: P.surface,
            border: `1px solid ${P.border}`,
            borderRadius: "14px",
            px: "18px",
            py: "12px",
            overflow: "auto",
            scrollbarWidth: "none",
            maxHeight: "65vh",
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <Avatar name={selectedName} size={40} />
            <Box>
              <Typography
                sx={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: P.text,
                  lineHeight: 1.2,
                }}
              >
                {selectedName}
              </Typography>
              <Typography sx={{ fontSize: 12, color: P.textSoft, mt: "2px" }}>
                {selectedType === "role"
                  ? "Role permissions"
                  : "User permission overrides"}
                {" · "}
                {permissions.length} permission
                {permissions.length !== 1 ? "s" : ""} across{" "}
                {Object.keys(groupedPermissions).length} module
                {Object.keys(groupedPermissions).length !== 1 ? "s" : ""}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
            {savedOk && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  color: P.green,
                  fontSize: 13,
                  fontWeight: 500,
                }}
              >
                <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />
                Saved
              </Box>
            )}

            {changedCount > 0 && (
              <Box
                sx={{
                  px: "10px",
                  py: "3px",
                  borderRadius: "20px",
                  background: P.amberLight,
                  border: `1px solid ${P.amberBorder}`,
                  color: P.amber,
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {changedCount} unsaved change{changedCount > 1 ? "s" : ""}
              </Box>
            )}

            <Button
              onClick={save}
              disabled={changedCount === 0 || saving}
              startIcon={
                saving ? (
                  <CircularProgress size={14} sx={{ color: "inherit" }} />
                ) : (
                  <SaveOutlinedIcon sx={{ fontSize: 16 }} />
                )
              }
              sx={{
                background: changedCount > 0 ? P.accent : P.border,
                color: changedCount > 0 ? "#fff" : P.textSoft,
                borderRadius: "9px",
                fontSize: 13,
                fontWeight: 600,
                px: "16px",
                py: "7px",
                textTransform: "none",
                boxShadow: "none",
                "&:hover": {
                  background: changedCount > 0 ? "#1D4ED8" : P.border,
                  boxShadow: "none",
                },
                "&:disabled": { background: P.border, color: P.textSoft },
              }}
            >
              Save changes
            </Button>
          </Box>
        </Box>
      </Stack>

      {/* Main layout */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          // height: "calc(100vh - 200px)",
          overflow: "auto",
          scrollbarWidth: "none",
          // minHeight: 560,
        }}
      >
        {/* ══════════ LEFT SIDEBAR ══════════ */}
        <Box
          sx={{
            width: 250,
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            // background: P.surface,
            border: `1px solid ${P.border}`,
            borderRadius: "14px",
            overflow: "hidden",
          }}
        >
          {/* Roles / Users tab switcher */}
          <Box
            sx={{
              display: "flex",
              gap: "5px",
              p: "8px",
              background: P.bg,
              borderBottom: `1px solid ${P.border}`,
            }}
          >
            {(["roles", "users"] as const).map((t) => {
              const active = activeTab === t;
              return (
                <Box
                  key={t}
                  onClick={() => {
                    setActiveTab(t);
                    setSelectedId("");
                    setPermissions([]);
                    setChangedPermissions({});
                    setSearch("");
                  }}
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "5px",
                    py: "7px",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontSize: 13,
                    fontWeight: 600,
                    transition: "all 0.12s",
                    // background: active ? P.surface : "transparent",
                    color: active ? P.text : P.textSoft,
                    border: active
                      ? `1px solid ${P.border}`
                      : "1px solid transparent",
                    boxShadow: active ? "0 1px 3px rgba(0,0,0,.06)" : "none",
                    userSelect: "none",
                    "&:hover": {
                      color: P.text,
                      background: active ? P.surface : "rgba(0,0,0,.03)",
                    },
                  }}
                >
                  {t === "roles" ? (
                    <AdminPanelSettingsOutlinedIcon sx={{ fontSize: 15 }} />
                  ) : (
                    <PeopleAltOutlinedIcon sx={{ fontSize: 15 }} />
                  )}
                  {t === "roles" ? "Roles" : "Users"}
                </Box>
              );
            })}
          </Box>

          {/* Search */}
          <Box
            sx={{
              px: "12px",
              py: "8px",
              borderBottom: `1px solid ${P.border}`,
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <SearchIcon sx={{ fontSize: 16, color: P.textSoft }} />
            <InputBase
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`Search ${activeTab}…`}
              sx={{ fontSize: 13, flex: 1, color: P.text }}
            />
          </Box>

          {/* List */}
          <Box sx={{ flex: 1, overflowY: "auto" }}>
            {listData.length === 0 && (
              <Typography
                sx={{
                  fontSize: 13,
                  color: P.textSoft,
                  p: 2,
                  textAlign: "center",
                }}
              >
                No results
              </Typography>
            )}
            {listData.map((item: any) => {
              const id = activeTab === "roles" ? item.role_id : item.user_id;
              const name =
                activeTab === "roles"
                  ? item.name
                  : `${item.first_name} ${item.last_name}`;
              const isSelected = selectedId === id;
              return (
                <Box
                  key={id}
                  onClick={() =>
                    loadPermissions(activeTab === "roles" ? "role" : "user", id)
                  }
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    px: "12px",
                    py: "9px",
                    cursor: "pointer",
                    borderBottom: `1px solid ${P.border}`,
                    borderRight: isSelected
                      ? `3px solid ${P.accent}`
                      : "3px solid transparent",
                    background: isSelected ? P.accentLight : "transparent",
                    transition: "all 0.12s",
                    "&:hover": {
                      background: isSelected ? P.accentLight : P.bg,
                    },
                  }}
                >
                  <Avatar name={name} size={30} />
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: isSelected ? 600 : 500,
                      color: isSelected ? P.accent : P.text,
                      flex: 1,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {name}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* ══════════ RIGHT PANEL ══════════ */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            minWidth: 0,
            overflow: "auto",
            scrollbarWidth: "none",
          }}
        >
          {/* ── Empty state ── */}
          {!selectedId ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                background: P.surface,
                border: `1px solid ${P.border}`,
                borderRadius: "14px",
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: "14px",
                  background: P.accentLight,
                  border: `1px solid ${P.accentBorder}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 26,
                }}
              >
                🔐
              </Box>
              <Typography sx={{ fontSize: 15, fontWeight: 600, color: P.text }}>
                Select a {activeTab === "roles" ? "role" : "user"}
              </Typography>
              <Typography sx={{ fontSize: 13, color: P.textSoft }}>
                Choose from the list on the left to manage permissions
              </Typography>
            </Box>
          ) : loading ? (
            /* ── Loading ── */
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: P.surface,
                border: `1px solid ${P.border}`,
                borderRadius: "14px",
              }}
            >
              <CircularProgress size={28} sx={{ color: P.accent }} />
            </Box>
          ) : (
            <Box
              sx={{ height: "65vh", overflow: "auto", scrollbarWidth: "none" }}
            >
              {/* ── Module accordion list (expanded by default) ── */}
              <Box
                sx={{
                  flex: 1,
                  overflow: "auto",
                  scrollbarWidth: "none",
                  // maxHeight: "65vh",
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                }}
              >
                {Object.keys(groupedPermissions).map((module) => {
                  const perms: any[] = groupedPermissions[module];
                  // const isCollapsed = !collapsedModules[module]; // default false = expanded
                  const isCollapsed = false; // default false = expanded

                  /* summary counts for the module header */
                  const enabledCount =
                    selectedType === "role"
                      ? perms.filter((p) => p.enabled).length
                      : perms.filter((p) => p.override_type === "allow").length;
                  const deniedCount =
                    selectedType === "user"
                      ? perms.filter((p) => p.override_type === "deny").length
                      : 0;
                  const inheritedCount =
                    selectedType === "user"
                      ? perms.filter(
                          (p) =>
                            !p.override_type || p.override_type === "inherit",
                        ).length
                      : 0;

                  return (
                    <Box
                      key={module}
                      sx={{
                        backgroundColor: "white",
                        border: `1px solid ${P.border}`,
                        borderRadius: "14px",
                        overflow: "auto",
                        scrollbarWidth: "none",
                        // transition: "border-color 0.15s",
                        // "&:hover": { borderColor: P.borderStrong },
                      }}
                    >
                      {/* Module header — always visible, click to collapse */}
                      <Box
                        onClick={() => toggleModule(module)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          px: "16px",
                          py: "12px",
                          cursor: "pointer",
                          userSelect: "none",
                          "&:hover": { background: "#FAFAFA" },
                        }}
                      >
                        <Box>
                          <Typography
                            sx={{
                              fontSize: 14,
                              fontWeight: 700,
                              color: P.text,
                              textTransform: "capitalize",
                            }}
                          >
                            {module}
                          </Typography>
                          <Typography
                            sx={{ fontSize: 12, color: P.textSoft, mt: "1px" }}
                          >
                            {perms.length} permission
                            {perms.length !== 1 ? "s" : ""}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          {/* Summary pills visible in header at all times */}
                          {selectedType === "role" ? (
                            <>
                              {enabledCount > 0 && (
                                <StatusPill
                                  label={`${enabledCount} enabled`}
                                  variant="green"
                                />
                              )}
                              {perms.length - enabledCount > 0 && (
                                <StatusPill
                                  label={`${perms.length - enabledCount} disabled`}
                                  variant="gray"
                                />
                              )}
                            </>
                          ) : (
                            <>
                              {enabledCount > 0 && (
                                <StatusPill
                                  label={`${enabledCount} allowed`}
                                  variant="green"
                                />
                              )}
                              {deniedCount > 0 && (
                                <StatusPill
                                  label={`${deniedCount} denied`}
                                  variant="red"
                                />
                              )}
                              {inheritedCount > 0 && (
                                <StatusPill
                                  label={`${inheritedCount} inherited`}
                                  variant="blue"
                                />
                              )}
                            </>
                          )}
                          <KeyboardArrowDownIcon
                            sx={{
                              fontSize: 20,
                              color: P.textSoft,
                              ml: "2px",
                              transition: "transform 0.2s",
                              transform: isCollapsed
                                ? "none"
                                : "rotate(180deg)",
                            }}
                          />
                        </Box>
                      </Box>

                      {/* Permission rows — open by default, collapse on click */}
                      {!isCollapsed && (
                        <Collapse in={!isCollapsed}>
                          <Divider sx={{ borderColor: P.border }} />
                          <Box
                            sx={{
                              p: "10px",
                              display: "flex",
                              flexDirection: "column",
                              gap: "8px",
                              // height: '100%',
                              overflow: "auto",
                              // backgroundColor: "red",
                            }}
                          >
                            {perms.map((p: any) => {
                              const overrideType: "inherit" | "allow" | "deny" =
                                p.override_type ?? "inherit";
                              const effective =
                                p.effective_allowed ?? p.enabled ?? false;

                              return (
                                <Box
                                  key={p.permission_id}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 2,
                                    px: "14px",
                                    py: "12px",
                                    borderRadius: "10px",
                                    background: P.bg,
                                    border: `1px solid ${P.border}`,
                                    flexWrap: "wrap",
                                    transition: "border-color 0.12s",
                                    "&:hover": { borderColor: P.borderStrong },
                                  }}
                                >
                                  {/* Left: name, description, status tags */}
                                  <Box sx={{ flex: 1, minWidth: 200 }}>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                        mb: "4px",
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          fontSize: 13,
                                          fontWeight: 600,
                                          color: P.text,
                                        }}
                                      >
                                        {p.name}
                                      </Typography>
                                      {p.permission_type && (
                                        <TypeBadge label={p.permission_type} />
                                      )}
                                    </Box>

                                    <Typography
                                      sx={{
                                        fontSize: 12,
                                        color: P.textSoft,
                                        mb: "6px",
                                      }}
                                    >
                                      {p.description ||
                                        "No description provided"}
                                    </Typography>

                                    {/* Status pills — user mode (same as original) */}
                                    {selectedType === "user" && (
                                      <Box
                                        sx={{
                                          display: "flex",
                                          gap: "5px",
                                          flexWrap: "wrap",
                                        }}
                                      >
                                        {overrideType === "inherit" && (
                                          <StatusPill
                                            label="Inherited"
                                            variant="gray"
                                          />
                                        )}
                                        {overrideType === "allow" && (
                                          <StatusPill
                                            label="Explicit Allow"
                                            variant="green"
                                          />
                                        )}
                                        {overrideType === "deny" && (
                                          <StatusPill
                                            label="Explicit Deny"
                                            variant="red"
                                          />
                                        )}
                                        <StatusPill
                                          label={
                                            effective
                                              ? "Access Granted"
                                              : "No Access"
                                          }
                                          variant={effective ? "green" : "gray"}
                                        />
                                      </Box>
                                    )}

                                    {/* Status pill — role mode */}
                                    {selectedType === "role" && (
                                      <StatusPill
                                        label={
                                          p.enabled ? "Enabled" : "Disabled"
                                        }
                                        variant={p.enabled ? "green" : "gray"}
                                      />
                                    )}
                                  </Box>

                                  {/* Right: controls */}
                                  {selectedType === "role" ? (
                                    /* Role — styled checkbox toggle */
                                    <Box
                                      onClick={() =>
                                        toggleRolePermission(p.permission_id)
                                      }
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "7px",
                                        cursor: "pointer",
                                        px: "12px",
                                        py: "7px",
                                        borderRadius: "8px",
                                        border: `1px solid ${p.enabled ? P.accent : P.border}`,
                                        background: p.enabled
                                          ? P.accentLight
                                          : P.surface,
                                        color: p.enabled
                                          ? P.accent
                                          : P.textSoft,
                                        fontSize: 13,
                                        fontWeight: 600,
                                        userSelect: "none",
                                        // height: "60vh",
                                        overflow: "auto",
                                        scrollbarWidth: "none",
                                        transition: "all 0.15s",
                                        "&:hover": {
                                          borderColor: P.accent,
                                          background: P.accentLight,
                                          color: P.accent,
                                        },
                                      }}
                                    >
                                      {p.enabled ? (
                                        <CheckBoxIcon sx={{ fontSize: 18 }} />
                                      ) : (
                                        <CheckBoxOutlineBlankIcon
                                          sx={{ fontSize: 18 }}
                                        />
                                      )}
                                      {p.enabled ? "Enabled" : "Disabled"}
                                    </Box>
                                  ) : (
                                    /* User — Inherit / Allow / Deny segmented control */
                                    <Box
                                      sx={{
                                        display: "flex",
                                        border: `1px solid ${P.border}`,
                                        borderRadius: "9px",
                                        overflow: "hidden",
                                        flexShrink: 0,
                                      }}
                                    >
                                      {(
                                        ["inherit", "allow", "deny"] as const
                                      ).map((opt, idx, arr) => {
                                        const isActive = overrideType === opt;
                                        const activeColors =
                                          opt === "allow"
                                            ? { bg: P.green, color: "#fff" }
                                            : opt === "deny"
                                              ? { bg: P.red, color: "#fff" }
                                              : { bg: P.text, color: "#fff" };
                                        return (
                                          <Box
                                            key={opt}
                                            onClick={() =>
                                              setUserOverride(
                                                p.permission_id,
                                                opt,
                                              )
                                            }
                                            sx={{
                                              px: "13px",
                                              py: "7px",
                                              cursor: "pointer",
                                              fontSize: 12,
                                              fontWeight: isActive ? 700 : 500,
                                              background: isActive
                                                ? activeColors.bg
                                                : P.surface,
                                              color: isActive
                                                ? activeColors.color
                                                : P.textSoft,
                                              borderRight:
                                                idx < arr.length - 1
                                                  ? `1px solid ${P.border}`
                                                  : "none",
                                              userSelect: "none",
                                              transition: "all 0.12s",
                                              "&:hover": {
                                                background: isActive
                                                  ? activeColors.bg
                                                  : P.bg,
                                                color: isActive
                                                  ? activeColors.color
                                                  : P.text,
                                              },
                                            }}
                                          >
                                            {opt.charAt(0).toUpperCase() +
                                              opt.slice(1)}
                                          </Box>
                                        );
                                      })}
                                    </Box>
                                  )}
                                </Box>
                              );
                            })}
                          </Box>
                        </Collapse>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default PermissionManagement;
